import { Grid2, TextField, Theme, Typography } from '@mui/material';
import { useOrganizationFeatureDialog } from 'features/organizations/hooks/useOrganizationFeatureDialog';
import { OrganizationPricing } from 'features/organizations/organizations.types';
import { ChangeEvent } from 'react';
import {
  Control,
  Controller,
  FormState,
  FieldPath,
  ControllerRenderProps,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatToUSDollars } from 'shared/utils';

interface OrganizationSeatsFormValues {
  regularSeats: number;
  proSeats: number;
}

type OrganizationSeatsFormFieldsProps<T extends OrganizationSeatsFormValues> = {
  control: Control<T>;
  formState: FormState<OrganizationSeatsFormValues>;
  isDisabled?: boolean;
  pricing?: OrganizationPricing;
  showLearnMore?: boolean;
};

const learnMoreStyle = {
  fontSize: ({ typography }: Theme) => typography.caption.fontSize,
  color: '#FF5722',
  textDecoration: 'underline',
  fontWeight: 600,
  cursor: 'pointer',
};

export const OrganizationSeatsFormFields = <
  T extends OrganizationSeatsFormValues,
>(
  props: OrganizationSeatsFormFieldsProps<T>,
): JSX.Element => {
  const {
    control,
    formState,
    isDisabled = false,
    pricing,
    showLearnMore,
  } = props;
  const { errors } = formState;
  const { t } = useTranslation();
  const { openDialog, FeatureDialog } = useOrganizationFeatureDialog();

  const handleChange = (
    field: ControllerRenderProps<T, FieldPath<T>>,
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    // trim leading 0s
    if (e.target.value.length > 1 && e.target.value.startsWith('0')) {
      e.target.value = e.target.value.replace(/^0+/, '');
    }
    field.onChange(e.target.value === '' ? '' : Number(e.target.value));
  };

  return (
    <>
      {FeatureDialog}
      <Grid2 container size={12}>
        <Grid2 size={8}>
          <Typography variant="h6">
            {t('organizationPurchase.member')}
          </Typography>
          {pricing && (
            <Typography variant="body2">
              {t('organizationPurchase.priceStartingAt', {
                price: formatToUSDollars(
                  pricing.seats.regular[0].unitAmount,
                  2,
                ),
              })}
            </Typography>
          )}
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name={'regularSeats' as FieldPath<T>}
            control={control}
            render={({ field }) => {
              const { ref, ...rest } = field;
              return (
                <TextField
                  {...rest}
                  inputRef={ref}
                  type="number"
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      inputMode: 'numeric',
                      'data-testid': 'regular-seats-input',
                    },
                  }}
                  error={Boolean(errors?.regularSeats)}
                  helperText={errors.regularSeats?.message}
                  fullWidth
                  disabled={isDisabled || formState.isSubmitting}
                  onChange={(e) => handleChange(field, e)}
                />
              );
            }}
          />
        </Grid2>
      </Grid2>
      {showLearnMore && (
        <Grid2>
          <Typography
            variant="all-small-caps"
            sx={learnMoreStyle}
            onClick={() => openDialog('regular')}
          >
            {t('organizationPurchase.learnMore')}
          </Typography>
        </Grid2>
      )}
      <Grid2 container size={12} alignItems="center">
        <Grid2 size={8}>
          <Typography variant="h6">{t('organizationPurchase.pro')}</Typography>
          {pricing && (
            <Typography variant="body2">
              {t('organizationPurchase.priceStartingAt', {
                price: formatToUSDollars(pricing.seats.pro[0].unitAmount, 2),
              })}
            </Typography>
          )}
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name={'proSeats' as FieldPath<T>}
            control={control}
            render={({ field }) => {
              const { ref, ...rest } = field;
              return (
                <TextField
                  {...rest}
                  inputRef={ref}
                  type="number"
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      inputMode: 'numeric',
                      'data-testid': 'pro-seats-input',
                    },
                  }}
                  error={Boolean(errors?.proSeats)}
                  helperText={errors.proSeats?.message}
                  fullWidth
                  disabled={isDisabled || formState.isSubmitting}
                  onChange={(e) => handleChange(field, e)}
                />
              );
            }}
          />
        </Grid2>
      </Grid2>
      {showLearnMore && (
        <Grid2>
          <Typography
            variant="all-small-caps"
            sx={learnMoreStyle}
            onClick={() => openDialog('pro')}
          >
            {t('organizationPurchase.learnMore')}
          </Typography>
        </Grid2>
      )}
    </>
  );
};
